import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy, PLATFORM_ID, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { extractApiErrorCode } from '../api/api-error';

@Injectable({ providedIn: 'root' })
export class BackendConnectivityService implements OnDestroy {
  private readonly offlineState = signal(false);
  private readonly backendUrl = environment.backendUrl.replace(/\/$/, '');
  private readonly browser: boolean;
  private readonly retryDelayMs = 3000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  readonly offline = this.offlineState.asReadonly();

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.browser = isPlatformBrowser(platformId);
  }

  recordSuccess(url: string): void {
    if (this.isBackendRequest(url)) {
      this.setOnline();
    }
  }

  recordError(url: string, error: HttpErrorResponse): void {
    if (!this.isBackendRequest(url)) {
      return;
    }

    if (this.isUnavailable(error)) {
      this.setOffline();
      return;
    }

    // An API error with a structured response still proves that the backend is reachable.
    this.setOnline();
  }

  retryNow(): void {
    if (!this.browser) {
      return;
    }

    this.clearRetryTimer();
    void this.probeBackend();
  }

  ngOnDestroy(): void {
    this.clearRetryTimer();
  }

  private isBackendRequest(url: string): boolean {
    return url === this.backendUrl || url.startsWith(`${this.backendUrl}/`);
  }

  private isUnavailable(error: HttpErrorResponse): boolean {
    // During a local rebuild the development server can briefly surface an
    // unstructured 404. Real API not-found responses always carry an ApiError code.
    return error.status === 404 && extractApiErrorCode(error) === null;
  }

  private setOffline(): void {
    this.offlineState.set(true);
    this.scheduleProbe();
  }

  private setOnline(): void {
    this.offlineState.set(false);
    this.clearRetryTimer();
  }

  private scheduleProbe(): void {
    if (!this.browser || this.retryTimer) {
      return;
    }

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.probeBackend();
    }, this.retryDelayMs);
  }

  private async probeBackend(): Promise<void> {
    try {
      const response = await fetch(`${this.backendUrl}/organisations/admin`, {
        cache: 'no-store',
        credentials: 'omit',
        method: 'GET',
      });

      if (![502, 503, 504].includes(response.status)) {
        this.setOnline();
        return;
      }
    } catch {
      // The retry schedule below keeps the banner visible until the backend responds.
    }

    this.setOffline();
  }

  private clearRetryTimer(): void {
    if (!this.retryTimer) {
      return;
    }

    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}
